// FIDIC Conditions of Contract for Construction, Second Edition 2017 ("Red Book
// 2017"). Assembles the complete, combined contract document a user lawfully
// authors: the Forms of Contract (Letter of Tender, Letter of Acceptance,
// Contract Agreement + witnessed signing page, DAAB Agreement), the Particular
// Conditions (Part A Contract Data + Part B Special Conditions), a Drawing
// Register appendix and section cover pages — all cross-referenced to the 2017
// General Conditions sub-clauses (which are FIDIC copyright and NOT reproduced).

import type {
  AssembledContract,
  Block,
  ContractPart,
  CoverSpec,
  ProjectBrief,
} from "./types";
import { amountToWords } from "./amountToWords";

const DASH = "________________";
function or(v: string | undefined | null, f = DASH): string {
  return v && String(v).trim() ? String(v) : f;
}
function money(m?: { amount: number; currency: string }): string {
  if (!m || m.amount == null || !isFinite(m.amount)) return DASH;
  return `${m.currency} ${m.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pct(n?: number): string {
  return n == null || !isFinite(n) ? "____%" : `${n}%`;
}
// Contract sum in words is ALWAYS derived from the figure so the two can never
// disagree. (A stored words string would go stale when the amount is changed.)
function priceWords(b: ProjectBrief): string {
  return amountToWords(b.contractPrice);
}
function tenderWords(b: ProjectBrief): string {
  return amountToWords(b.contractPrice);
}
/** How VAT applies to the Contract Price (defaults to exclusive — the FIDIC norm). */
function vatTreatment(b: ProjectBrief): string {
  return b.priceVat === "inclusive" ? "inclusive of Value Added Tax (VAT)" : "exclusive of Value Added Tax (VAT)";
}
/** Rate of exchange basis for payment (FIDIC Sub-Clause 14.15) — prevailing market
 * rate (default) or a rate fixed in the Contract. */
function exchangeRateText(b: ProjectBrief): string {
  if (b.exchangeRateBasis === "fixed") {
    const r = b.fixedExchangeRate?.trim();
    return r
      ? `Fixed for the duration of the Contract at ${r}; not subject to adjustment for currency fluctuation.`
      : "Fixed for the duration of the Contract at the rate(s) stated in the Contract; not subject to adjustment for currency fluctuation.";
  }
  const src = b.exchangeRateSource?.trim();
  return src
    ? `Prevailing market rate — ${src}.`
    : "Prevailing market rate ruling at the date of payment, as published by the central bank of the country of the Site.";
}

// --- Part A: Contract Data (FIDIC 2017 sub-clause references) ----------------
function contractDataRows(b: ProjectBrief): string[][] {
  const e = b.employer, c = b.contractor, eng = b.engineer;
  return [
    ["1.1.27", "Defects Notification Period (DNP)", or(b.defectsNotification)],
    ["1.1.31", "Employer's name and address", `${or(e.name)}, ${or(e.address)}`],
    ["1.1.35", "Engineer's name and address", eng?.name ? `${eng.name}, ${or(eng.address)}` : "N/a"],
    ["1.1.84", "Time for Completion of the Works", or(b.timeForCompletion)],
    ["1.3(a)(ii)", "Agreed methods of electronic transmission", or(b.communicationsLanguage ? "Electronic platform" : undefined, "Electronic platform (as agreed)")],
    ["1.3(d)", "Address of Employer for communications", or(e.email || e.address)],
    ["1.3(d)", "Address of Engineer for communications", or(eng?.email || eng?.address, "N/a")],
    ["1.3(d)", "Address of Contractor for communications", or(c.email || c.address)],
    ["1.4", "Contract shall be governed by the law of", or(b.governingLaw)],
    ["1.4", "Ruling language", or(b.rulingLanguage)],
    ["1.4", "Language for communications", or(b.communicationsLanguage || b.rulingLanguage)],
    ["2.1", "Right of access to and possession of the Site", "All of the Site, as required after the Letter of Acceptance"],
    ["14.1", "The Accepted Contract Amount / Contract Price is", `${money(b.contractPrice)}, ${vatTreatment(b)}`],
    ["4.2", "Performance Security (percentage of the Accepted Contract Amount)", pct(b.performanceSecurityPercent)],
    ["5.1", "Maximum value of work subcontracted", "Any subcontract works shall be approved by the Engineer / project consultant"],
    ["6.5", "Normal working hours on the Site", "7am – 5pm (varies as required)"],
    ["8.8", "Delay Damages payable for each day of delay", b.delayDamagesPerDay ? money(b.delayDamagesPerDay) : DASH],
    ["8.8", "Maximum amount of Delay Damages", `${pct(b.delayDamagesCapPercent)} of the Contract Price`],
    ["12.2", "Method of measurement", "Imperial"],
    ["13.4(b)(ii)", "Percentage for overhead and profit on Provisional Sums", pct(b.overheadProfitPercent)],
    ["14.2", "Total amount of Advance Payment (percentage of Accepted Contract Amount)", pct(b.advancePaymentPercent)],
    ["14.2", "Currency of Advance Payment", or(b.contractPrice.currency)],
    ["14.2", "Advance Payment disbursement", advanceDisbursementText(b)],
    ["14.2.3", "Repayment of the Advance Payment", or(b.advanceRepayment)],
    ["14.3", "Period of payment", b.paymentPeriodDays ? `${b.paymentPeriodDays} days` : DASH],
    ["14.3(iii)", "Percentage of retention", pct(b.retentionPercent)],
    ["14.3(iii)", "Limit of Retention Money (percentage of Accepted Contract Amount)", pct(b.retentionCapPercent)],
    ["14.6.2", "Minimum amount of Interim Payment Certificate (IPC)", money(b.minimumIpc)],
    ["14.15", "Currency of payment", or(b.paymentCurrencies || b.contractPrice.currency)],
    ["14.15", "Rate of exchange", exchangeRateText(b)],
    ["21.1", `Time for appointment of the ${boardAbbr(b)}`, b.daab?.appointmentWeeks ? `${b.daab.appointmentWeeks} weeks` : "4 weeks"],
    ["21.1", `The ${boardAbbr(b)} shall comprise`, `${b.daab?.members || 3} member(s)${b.daab?.registrationBody ? `, registered with ${b.daab.registrationBody}` : ""}`],
  ];
}

function advanceDisbursementText(b: ProjectBrief): string {
  const n = b.advanceSchedule?.length || b.advanceInstallments;
  const parts: string[] = [];
  const first = b.advanceFixedStart || (b.advanceSchedule?.[0]?.amount);
  if (first && isFinite(first.amount)) parts.push(`First disbursement fixed at ${money(first)}`);
  if (n && isFinite(n)) parts.push(`disbursed in ${n} installment(s)`);
  if (b.advanceSchedule?.length) return parts.join(", ") + " per the Advance Payment schedule in Part B (Special Conditions)";
  if (!parts.length) return b.advancePaymentPercent != null ? "Disbursed on provision of the Advance Payment Guarantee" : "____________________";
  return parts.join(", ") + ", each issued through an Advance Payment Certificate";
}

function boardAbbr(b: ProjectBrief): "DAAB" | "DRB" {
  return b.daab?.kind === "DRB" ? "DRB" : "DAAB";
}
function boardFull(b: ProjectBrief): string {
  return b.daab?.kind === "DRB" ? "Dispute Resolution Board" : "Dispute Avoidance/Adjudication Board";
}

// --- Forms of Contract -------------------------------------------------------
function letterOfTender(b: ProjectBrief): Block[] {
  return [
    { type: "heading", text: "Letter of Tender", level: 1 },
    { type: "paragraph", text: `NAME OF CONTRACT: ${or(b.projectName)}${b.contractReference ? `  (Contract No. ${b.contractReference})` : ""}` },
    { type: "paragraph", text: `TO: ${or(b.employer.name)}` },
    {
      type: "paragraph",
      text: "We have examined the Conditions of Contract, Specification, Drawings, Schedules including the Bill of Quantities, the Contract Data and Addenda for the above-named Contract, and the words and expressions used herein shall have the meanings assigned to them in the Conditions of Contract. We offer to execute and complete the Works and remedy any defects therein, in conformity with this Tender which includes all these documents, for the sum of:",
    },
    { type: "paragraph", text: `${tenderWords(b) ? tenderWords(b) + " — " : ""}${money(b.contractPrice)}, ${vatTreatment(b)},` },
    { type: "paragraph", text: "or such other amount as may be determined in accordance with the Contract." },
    { type: "paragraph", text: "If this offer is accepted, we will provide the specified Performance Security, commence the Works as soon as is reasonably practicable after the Commencement Date, and complete the Works in accordance with the above-named documents within the Time for Completion." },
    { type: "paragraph", text: "Unless and until a Contract Agreement is prepared and executed, this Letter of Tender together with your written acceptance thereof shall constitute a binding contract between us." },
    { type: "signblock", parties: [{ role: "Contractor", name: b.contractor.representative, title: b.contractor.representativeTitle }] },
  ];
}

function letterOfAcceptance(b: ProjectBrief): Block[] {
  return [
    { type: "heading", text: "Letter of Acceptance", level: 1 },
    { type: "paragraph", text: `NAME OF CONTRACT: ${or(b.projectName)}` },
    { type: "paragraph", text: `CONTRACT NUMBER: ${or(b.contractReference)}` },
    { type: "paragraph", text: `TO: ${or(b.contractor.name)}` },
    { type: "paragraph", text: "We thank you for your Tender for the execution and completion of the Works comprising the above-named Contract and the remedying of defects therein, all in conformity with the terms and conditions contained in the Contract." },
    { type: "paragraph", text: `We have pleasure in accepting your Tender for the Accepted Contract Amount of ${tenderWords(b) ? tenderWords(b) + " — " : ""}${money(b.contractPrice)}, ${vatTreatment(b)}.` },
    { type: "paragraph", text: "In consideration of you properly and truly performing the Contract, we agree to pay you the Accepted Contract Amount, or such other sums to which you may become entitled under the terms of the Contract, at such times and in such manner as prescribed by the Contract." },
    { type: "paragraph", text: "We acknowledge that this Letter of Acceptance creates a binding Contract between us." },
    { type: "signblock", parties: [{ role: "Employer", name: b.employer.representative, title: b.employer.representativeTitle }] },
  ];
}

function contractAgreement(b: ProjectBrief): Block[] {
  const docs = [
    "(a) the Letter of Acceptance;",
    "(b) the Letter of Tender;",
    b.addenda?.length ? `(c) the Addenda Nos ${b.addenda.join(", ")};` : "(c) the Addenda (if any);",
    "(d) the Particular Conditions — Part A (Contract Data) and Part B (Special Conditions);",
    "(e) the General Conditions (FIDIC Conditions of Contract for Construction, Second Edition 2017);",
    "(f) the Specification;",
    "(g) the Drawings; and",
    "(h) the Schedules and any other documents forming part of the Contract.",
  ];
  return [
    { type: "heading", text: "Contract Agreement", level: 1 },
    { type: "paragraph", text: `This Agreement is made the ${or(b.agreementDate, "____ day of ____________, 20__")}.` },
    { type: "paragraph", text: `Between ${or(b.employer.name)} (hereinafter called "the Employer") of the one part, and ${or(b.contractor.name)} (hereinafter called "the Contractor") of the other part.` },
    { type: "paragraph", text: `Whereas the Employer desires that the Works known as ${or(b.projectName)}${b.contractReference ? ` (Contract No. ${b.contractReference})` : ""} should be executed by the Contractor, and has accepted a Tender by the Contractor for the execution and completion of these Works and the remedying of any defects therein.` },
    { type: "paragraph", text: "The Employer and the Contractor agree as follows:" },
    { type: "clause", text: "1.  In this Agreement, words and expressions shall have the same meanings as are respectively assigned to them in the Conditions of Contract hereinafter referred to." },
    { type: "clause", text: "2.  The following documents shall be deemed to form and be read and construed as part of this Agreement:" },
    ...docs.map((d): Block => ({ type: "clause", ref: "", text: "     " + d })),
    { type: "clause", text: `3.  In consideration of the payments to be made by the Employer to the Contractor, the Contractor hereby covenants with the Employer to execute and complete the Works and remedy any defects therein, in conformity with the provisions of the Contract.` },
    { type: "clause", text: `4.  The Employer hereby covenants to pay the Contractor, in consideration of the execution and completion of the Works and the remedying of defects therein, the Contract Price of ${money(b.contractPrice)}${priceWords(b) ? ` (${priceWords(b)})` : ""}, ${vatTreatment(b)}, at the times and in the manner prescribed by the Contract.` },
    { type: "paragraph", text: "In Witness whereof the parties hereto have caused this Agreement to be executed the day and year first before written, in accordance with their respective laws." },
    {
      type: "signblock",
      parties: [
        { role: "Employer", name: b.employer.representative, title: b.employer.representativeTitle, witnessName: b.employer.witnessName, witnessAddress: b.employer.witnessAddress },
        { role: "Contractor", name: b.contractor.representative, title: b.contractor.representativeTitle, witnessName: b.contractor.witnessName, witnessAddress: b.contractor.witnessAddress },
      ],
    },
  ];
}

function daabAgreement(b: ProjectBrief): Block[] {
  const d = b.daab;
  const AB = boardAbbr(b); // "DAAB" | "DRB"
  const FULL = boardFull(b);
  const isDrb = AB === "DRB";
  return [
    { type: "heading", text: `${FULL} (${AB}) Agreement`, level: 1 },
    { type: "paragraph", text: `Name and details of the Contract: ${or(b.projectName)}${b.contractReference ? ` — ${b.contractReference}` : ""}.` },
    { type: "paragraph", text: `This Agreement is made between the Employer, ${or(b.employer.name)}, and the Contractor, ${or(b.contractor.name)}.` },
    { type: "heading", text: "1. Composition and Appointment of Members", level: 2 },
    { type: "clause", text: `1.1  The ${AB} shall consist of ${d?.members || 3} member(s)${d?.registrationBody ? `, all of whom shall be qualified professional engineers registered with ${d.registrationBody}` : ""}.` },
    { type: "clause", text: `1.2  Each Party shall appoint one member of the ${AB} and notify the other Party in writing.` },
    { type: "clause", text: `1.3  The third member, who shall serve as Chairperson, shall be appointed through random selection from a pool of eligible engineers who have no current or prior affiliation with either Party.` },
    { type: "clause", text: `1.4  If a Party fails to appoint its member within ${d?.appointmentWeeks || 4} weeks, or the Parties cannot agree on the Chairperson, the matter shall be referred to ${d?.registrationBody || "the relevant professional body"} for resolution.` },
    { type: "heading", text: "2. Engagement and Binding Nature of Decisions", level: 2 },
    { type: "clause", text: `2.1  Each ${AB} member shall enter into a tripartite agreement with the Employer and the Contractor.` },
    isDrb
      ? { type: "clause", text: `2.2  Disputes of a professional or technical nature shall first be referred to the ${AB}. If the ${AB}'s ruling is unacceptable to either Party, the dispute may be escalated to arbitration under Clause 21 [Disputes and Arbitration] as amended by the Particular Conditions. The costs of the ${AB} shall be shared equally by the Parties unless otherwise agreed.` }
      : { type: "clause", text: `2.2  The decisions of the ${AB} shall be binding on both Parties unless and until revised in arbitration under Clause 21 [Disputes and Arbitration].` },
    { type: "paragraph", text: `This Agreement shall be governed by the law of ${or(b.governingLaw)}. The ${AB} members shall be paid in accordance with Clause 9 of the General Conditions; the currency of payment shall be ${or(b.contractPrice.currency)}. The Employer and the Contractor shall be jointly and severally liable for the ${AB} members' fees.` },
    {
      type: "signblock",
      parties: [
        { role: "Employer", name: b.employer.representative },
        { role: "Contractor", name: b.contractor.representative },
      ],
    },
  ];
}

// --- Part builders -----------------------------------------------------------
function cover(kicker: string, title: string, b: ProjectBrief, subtitle?: string): CoverSpec {
  return {
    kicker,
    title,
    subtitle: subtitle || b.projectName,
    meta: [
      { label: "Project", value: or(b.projectName) },
      { label: "Contract No.", value: or(b.contractReference) },
      { label: "Standard", value: "FIDIC Red Book — Second Edition 2017" },
      { label: "Employer", value: or(b.employer.name) },
      { label: "Contractor", value: or(b.contractor.name) },
    ],
  };
}

export function assembleFidic2017(b: ProjectBrief): AssembledContract {
  const parts: ContractPart[] = [];

  // Section 1 — Forms of Contract
  parts.push({
    id: "forms",
    name: "Forms of Contract",
    cover: cover("Section 1", "Forms of Contract", b, `Letter of Tender · Letter of Acceptance · Contract Agreement · ${boardAbbr(b)} Agreement`),
    blocks: [
      ...letterOfTender(b),
      { type: "pagebreak" },
      ...letterOfAcceptance(b),
      { type: "pagebreak" },
      ...contractAgreement(b),
      { type: "pagebreak" },
      ...daabAgreement(b),
    ],
  });

  // Section 2 — Conditions of Contract (General) — referenced, not reproduced
  parts.push({
    id: "general",
    name: "Conditions of Contract (General)",
    cover: cover("Section 2", "General Conditions", b, "FIDIC Conditions of Contract for Construction, 2nd Ed. 2017"),
    blocks: [
      { type: "heading", text: "General Conditions of Contract", level: 1 },
      { type: "paragraph", text: "The General Conditions of Contract are the FIDIC Conditions of Contract for Construction, Second Edition 2017 (Clauses 1 to 21 inclusive), together with the General Conditions of the Dispute Avoidance/Adjudication Agreement appended thereto." },
      { type: "paragraph", text: "The General Conditions are incorporated into this Contract by reference and are amended and completed by the Particular Conditions (Part A — Contract Data and Part B — Special Conditions) that follow. The printed General Conditions are the copyright of FIDIC and are to be read together with this document; they are not reproduced here." },
      { type: "paragraph", text: "In the event of any conflict, the order of priority of the Contract documents shall be as stated in the Contract Agreement and Sub-Clause 1.5 [Priority of Documents]." },
    ],
  });

  // Section 3 — Particular Conditions Part A (Contract Data)
  parts.push({
    id: "partA",
    name: "Particular Conditions — Part A (Contract Data)",
    cover: cover("Section 3", "Particular Conditions — Part A", b, "Contract Data"),
    blocks: [
      { type: "heading", text: "Particular Conditions — Part A: Contract Data", level: 1 },
      { type: "paragraph", text: "The following Contract Data completes and amends the General Conditions. Each entry is keyed to the Sub-Clause of the General Conditions to which it relates. Where an entry is left blank, the corresponding provision of the General Conditions applies without amendment." },
      { type: "table", columns: ["Sub-Clause", "Data to be given", "Data"], rows: contractDataRows(b) },
    ],
  });

  // Section 4 — Particular Conditions Part B (Special Conditions)
  const partBBlocks: Block[] = [
    { type: "heading", text: "Particular Conditions — Part B: Special Conditions", level: 1 },
    { type: "paragraph", text: "The following Special Conditions amend, add to, delete from or replace clauses of the General Conditions. In the event of any conflict between Part A and Part B, Part B prevails; between the Particular Conditions and the General Conditions, the Particular Conditions prevail." },
  ];
  // Explicit, data-driven Advance Payment clause so the installment structure
  // (and each installment's trigger) is always written out and never lost.
  if (b.advanceSchedule && b.advanceSchedule.length) {
    partBBlocks.push({ type: "heading", text: "Advance Payment (Sub-Clause 14.2)", level: 2 });
    partBBlocks.push({
      type: "paragraph",
      text: `Sub-Clause 14.2 [Advance Payment] is amended as follows. The Advance Payment${b.advancePaymentPercent != null ? ` of ${b.advancePaymentPercent}% of the Accepted Contract Amount` : ""} shall be disbursed in ${b.advanceSchedule.length} installment(s) as set out below, subject to the Contractor providing the Advance Payment Guarantee under Sub-Clause 14.2.1:`,
    });
    partBBlocks.push({
      type: "list",
      items: b.advanceSchedule.map((inst, i) => ({
        prefix: `(${String.fromCharCode(97 + i)})`,
        text: `${inst.label ? inst.label + " — " : ""}${inst.amount && isFinite(inst.amount.amount) ? money(inst.amount) : inst.percent != null ? `${inst.percent}% of the Accepted Contract Amount` : "____________"}${inst.trigger ? `, payable ${inst.trigger}` : ""}.`,
      })),
    });
    partBBlocks.push({ type: "paragraph", text: `The Advance Payment shall be recovered by deductions from Interim Payment Certificates in accordance with Sub-Clause 14.2.3${b.advanceRepayment ? ` (${b.advanceRepayment})` : ""}, and shall be fully recovered before issue of the Taking-Over Certificate.` });
  }
  if (b.daab?.kind === "DRB") {
    partBBlocks.push({ type: "heading", text: "Amendment to Clause 21 — Dispute Board", level: 2 });
    partBBlocks.push({
      type: "paragraph",
      text: `Clause 21 [Disputes and Arbitration] is amended so that references to the Dispute Avoidance/Adjudication Board (DAAB) shall be read as references to a Dispute Resolution Board (DRB) constituted under ${or(b.daab?.registrationBody, "the relevant professional body")}. Disputes of a professional or technical nature shall first be referred to the DRB; if the DRB's ruling is unacceptable to either Party, the dispute may be escalated to arbitration under Clause 21. The costs of the DRB shall be shared equally by the Parties unless otherwise agreed.`,
    });
  }
  for (const n of b.narrative || []) {
    partBBlocks.push({ type: "heading", text: `${n.marker ? n.marker + ". " : ""}${n.heading}`, level: 2 });
    partBBlocks.push({ type: "paragraph", text: n.text });
  }
  if (b.specialProvisions.length) {
    partBBlocks.push({ type: "heading", text: "Amendments to the General Conditions", level: 2 });
    b.specialProvisions.forEach((sp, i) => {
      partBBlocks.push({ type: "heading", text: `SC${i + 1}. ${sp.heading}${sp.clauseRef ? `  (Sub-Clause ${sp.clauseRef})` : ""}`, level: 3 });
      if (sp.kind && sp.clauseRef) {
        const verb = sp.kind === "delete" ? "is deleted" : sp.kind === "replace" ? "is deleted and replaced with the following" : sp.kind === "amend" ? "is amended as follows" : "is supplemented by adding the following";
        partBBlocks.push({ type: "paragraph", text: `Sub-Clause ${sp.clauseRef} ${verb}:` });
      }
      partBBlocks.push({ type: "paragraph", text: sp.text });
    });
  }
  parts.push({
    id: "partB",
    name: "Particular Conditions — Part B (Project Particulars)",
    cover: cover("Section 4", "Particular Conditions — Part B", b, "Project Particulars & Special Provisions"),
    blocks: partBBlocks,
  });

  // Section 5 — Special Conditions of Contract (standalone document). Fold in any
  // specialConditionsAdd items (assistant "add under the Special Conditions"
  // requests) as numbered clauses so they ALWAYS render — even if an older client
  // stored them as the raw field instead of appending to specialConditionsBlocks.
  const scBlocks: Block[] = [...(b.specialConditionsBlocks || [])];
  if (Array.isArray(b.specialConditionsAdd) && b.specialConditionsAdd.length) {
    let next = scBlocks
      .filter((bl): bl is Extract<Block, { type: "heading" }> => bl.type === "heading")
      .reduce((mx, bl) => { const m = /^Clause\s+(\d+)/i.exec(bl.text || ""); return m ? Math.max(mx, parseInt(m[1], 10)) : mx; }, 0) + 1;
    const addBlocks: Block[] = [];
    for (const it of b.specialConditionsAdd) {
      if (!it || (!it.text && !it.heading)) continue;
      addBlocks.push({ type: "heading", text: `Clause ${next} — ${(it.heading || "").trim() || "Special Condition"}`, level: 2 });
      addBlocks.push({ type: "paragraph", text: (it.text || "").trim() });
      next++;
    }
    // Insert before any trailing "Final Provisions" closing block.
    const fpIdx = scBlocks.findIndex((bl) => bl.type === "heading" && /final provisions/i.test((bl as any).text || ""));
    if (fpIdx >= 0) scBlocks.splice(fpIdx, 0, ...addBlocks);
    else scBlocks.push(...addBlocks);
  }
  if (scBlocks.length) {
    parts.push({
      id: "specialConditions",
      name: b.specialConditionsTitle || "Special Conditions of Contract",
      cover: cover("Section 5", b.specialConditionsTitle || "Special Conditions of Contract", b, b.specialConditionsDate ? `Dated ${b.specialConditionsDate}` : "Special Conditions of Contract"),
      blocks: [
        { type: "heading", text: b.specialConditionsTitle || "Special Conditions of Contract", level: 1 },
        ...(b.specialConditionsDate ? [{ type: "paragraph", text: `${or(b.projectName)} — ${b.specialConditionsDate}` } as Block] : []),
        { type: "paragraph", text: "These Special Conditions supplement and, where inconsistent, supersede the General Conditions of Contract, subject to the priority of documents set out below." },
        ...scBlocks,
      ],
    });
  }

  // Appendix A — Drawing Register
  parts.push({
    id: "appxDrawings",
    name: "Appendix A — Drawing Register",
    cover: cover("Appendix A", "Drawing Register", b, "Contract Drawings incorporated under Sub-Clause 1.1"),
    blocks: [
      { type: "heading", text: "Appendix A — Drawing Register", level: 1 },
      b.drawings.length
        ? { type: "paragraph", text: `The following ${b.drawings.length} Drawings form part of the Contract and are incorporated by reference. Each Drawing is the revision listed; any later revision issued by the Engineer supersedes the revision noted here.` }
        : { type: "paragraph", text: "No Drawings have been listed. Insert the Drawing Register before execution." },
      ...(b.drawings.length
        ? [{ type: "table", columns: ["#", "Drawing No.", "Title", "Rev.", "Discipline"], rows: b.drawings.map((d, i) => [String(i + 1), d.number || "—", d.title || "—", d.revision || "—", d.discipline || "—"]) } as Block]
        : []),
    ],
  });

  // Appendix B — Specifications & Schedules (referenced)
  parts.push({
    id: "appxSpecs",
    name: "Appendix B — Specifications & Schedules",
    cover: cover("Appendix B", "Specifications & Schedules", b, "Bill of Quantities and Technical Specifications"),
    blocks: [
      { type: "heading", text: "Appendix B — Specifications & Schedules", level: 1 },
      { type: "paragraph", text: "The Bill of Quantities and the technical Specifications listed below form part of the Contract and are provided under separate cover." },
      { type: "table", columns: ["Ref.", "Document"], rows: (b.specifications?.length ? b.specifications : [{ title: "Bill of Quantities", reference: "BOQ" }, { title: "Technical Specification", reference: "SPEC" }]).map((s) => [s.reference || "—", s.title]) },
    ],
  });

  return {
    title: `${or(b.projectName, "Construction Contract")}`,
    standardLabel: "FIDIC Conditions of Contract for Construction, Second Edition 2017 (Red Book)",
    masterCover: {
      title: or(b.projectName, "Construction Contract"),
      subtitle: or(b.projectLocation),
      contractLabel: "CONSTRUCTION CONTRACT",
      standardLine: "FIDIC RED BOOK — SECOND EDITION 2017",
      date: or(b.coverDate || b.agreementDate, ""),
      forParty: or(b.employer.name),
      preparedBy: [
        ...(b.preparedBy?.name ? [b.preparedBy.name] : []),
        ...(b.preparedBy?.org ? [b.preparedBy.org] : b.engineer?.name ? [b.engineer.name] : []),
        ...(b.preparedBy?.role ? [b.preparedBy.role] : []),
        ...(b.preparedBy?.address ? [b.preparedBy.address] : []),
      ],
      projectNo: b.contractReference ? `Project No. ${b.contractReference}` : "",
    },
    parts,
    notice:
      "This document set contains only the project-specific Forms of Contract, Particular Conditions and appendices, which the parties author. The FIDIC General Conditions (Clauses 1–21) are incorporated by reference and are NOT reproduced here; they are the copyright of FIDIC and must be obtained from FIDIC (fidic.org). Not legal advice — have the finished contract reviewed by qualified counsel before execution.",
  };
}
